export const steps = [
  {
    num: "01", icon: "🌐",
    title: "Choose Your Destination",
    desc: "Browse 180+ countries with live availability, processing timelines, and fee breakdowns. Find exactly the visa type your journey requires.",
  },
  {
    num: "02", icon: "📄",
    title: "Upload Your Documents",
    desc: "Securely submit your passport scan and supporting documents through our encrypted portal. Our checklist ensures nothing is missed.",
  },
  {
    num: "03", icon: "⚙️",
    title: "We Process Your Application",
    desc: "Dedicated visa consultants review, verify, and submit your application to the relevant embassy — with real-time status updates at every step.",
  },
  {
    num: "04", icon: "⏰",
    title: "Visa Delivered On Time",
    desc: "Receive your approved visa digitally, delivered before your travel date. On-time guarantee — or we refund you in full.",
  },
];

export const passportCountries = [
  "Afghanistan", "Albania", "Algeria", "Andorra", "Angola", "Antigua and Barbuda",
  "Argentina", "Armenia", "Australia", "Austria", "Azerbaijan", "Bahamas", "Bahrain",
  "Bangladesh", "Barbados", "Belarus", "Belgium", "Belize", "Benin", "Bhutan",
  "Bolivia", "Bosnia and Herzegovina", "Botswana", "Brazil", "Brunei", "Bulgaria",
  "Burkina Faso", "Burundi", "Cabo Verde", "Cambodia", "Cameroon", "Canada",
  "Central African Republic", "Chad", "Chile", "China", "Colombia", "Comoros",
  "Congo (DRC)", "Congo (Republic)", "Costa Rica", "Croatia", "Cuba", "Cyprus",
  "Czech Republic", "Denmark", "Djibouti", "Dominica", "Dominican Republic",
  "Ecuador", "Egypt", "El Salvador", "Equatorial Guinea", "Eritrea", "Estonia",
  "Eswatini", "Ethiopia", "Fiji", "Finland", "France", "Gabon", "Gambia",
  "Georgia", "Germany", "Ghana", "Greece", "Grenada", "Guatemala", "Guinea",
  "Guinea-Bissau", "Guyana", "Haiti", "Honduras", "Hungary", "Iceland", "India",
  "Indonesia", "Iran", "Iraq", "Ireland", "Israel", "Italy", "Jamaica", "Japan",
  "Jordan", "Kazakhstan", "Kenya", "Kiribati", "Kosovo", "Kuwait", "Kyrgyzstan",
  "Laos", "Latvia", "Lebanon", "Lesotho", "Liberia", "Libya", "Liechtenstein",
  "Lithuania", "Luxembourg", "Madagascar", "Malawi", "Malaysia", "Maldives",
  "Mali", "Malta", "Marshall Islands", "Mauritania", "Mauritius", "Mexico",
  "Micronesia", "Moldova", "Monaco", "Mongolia", "Montenegro", "Morocco",
  "Mozambique", "Myanmar", "Namibia", "Nauru", "Nepal", "Netherlands",
  "New Zealand", "Nicaragua", "Niger", "Nigeria", "North Korea", "North Macedonia",
  "Norway", "Oman", "Pakistan", "Palau", "Palestine", "Panama", "Papua New Guinea",
  "Paraguay", "Peru", "Philippines", "Poland", "Portugal", "Qatar", "Romania",
  "Russia", "Rwanda", "Saint Kitts and Nevis", "Saint Lucia",
  "Saint Vincent and the Grenadines", "Samoa", "San Marino", "São Tomé and Príncipe",
  "Saudi Arabia", "Senegal", "Serbia", "Seychelles", "Sierra Leone", "Singapore",
  "Slovakia", "Slovenia", "Solomon Islands", "Somalia", "South Africa", "South Korea",
  "South Sudan", "Spain", "Sri Lanka", "Sudan", "Suriname", "Sweden", "Switzerland",
  "Syria", "Taiwan", "Tajikistan", "Tanzania", "Thailand", "Timor-Leste", "Togo",
  "Tonga", "Trinidad and Tobago", "Tunisia", "Turkey", "Turkmenistan", "Tuvalu",
  "Uganda", "Ukraine", "United Arab Emirates", "United Kingdom", "United States",
  "Uruguay", "Uzbekistan", "Vanuatu", "Vatican City", "Venezuela", "Vietnam",
  "Yemen", "Zambia", "Zimbabwe",
];

export const visaCountries = [
  {
    code: "GB",
    name: "United Kingdom",
    region: "Europe",
    visaType: "Standard Visitor Visa",
    maxScore: 80,
    questions: [
      {
        id: "purpose",
        weight: 10,
        text: "What is the primary purpose of your visit to the United Kingdom?",
        options: [
          { label: "Tourism and sightseeing", score: 10 },
          { label: "Visiting family or close friends", score: 8, note: "Invitation letter from host required" },
          { label: "Attending a conference or business meeting", score: 7, note: "Employer letter and registration confirmation needed" },
          { label: "Medical treatment", score: 6, note: "Hospital appointment letter is mandatory" },
        ],
        improvementNote: "Tourism is the strongest basis for a Standard Visitor application. If visiting family, ensure your host provides a detailed invitation letter and proof of their UK immigration status.",
      },
      {
        id: "finances",
        weight: 15,
        text: "How would you describe your available funds for this trip?",
        options: [
          { label: "I have 6+ months of consistent bank statements showing sufficient funds", score: 15 },
          { label: "I have 3–6 months of statements with adequate balance", score: 10 },
          { label: "My statements are less than 3 months old", score: 5 },
          { label: "A sponsor is covering my costs", score: 6, note: "Sponsor must provide their own financial evidence" },
        ],
        improvementNote: "UKVI officers place heavy emphasis on financial stability. Six months of statements showing regular salary credits and a comfortable balance — typically £2,500–£5,000 per week of travel — significantly strengthens your application.",
      },
      {
        id: "travel_history",
        weight: 10,
        text: "What best describes your international travel history?",
        options: [
          { label: "I hold or have held a valid US, Schengen, or Canadian visa", score: 10 },
          { label: "I have travelled to multiple countries in the past 3 years", score: 8 },
          { label: "I have travelled abroad once or twice", score: 5 },
          { label: "This would be my first international trip", score: 2 },
        ],
        improvementNote: "Prior travel to trusted visa countries (USA, Schengen, Canada, Australia) is one of the strongest positive indicators for UK visa officers. Build a travel history with easier-to-obtain visas first.",
      },
      {
        id: "employment",
        weight: 15,
        text: "What is your current employment or occupation status?",
        options: [
          { label: "Salaried employee with a stable employer for 2+ years", score: 15 },
          { label: "Self-employed with registered business and filed taxes", score: 11 },
          { label: "Recently employed (under 1 year) or student with sponsorship", score: 7 },
          { label: "Retired with pension income", score: 12 },
          { label: "Unemployed or between jobs", score: 2 },
        ],
        improvementNote: "Stable employment demonstrates both financial capacity and a compelling reason to return home. If recently employed, include an employer letter confirming your position and approved leave dates.",
      },
      {
        id: "home_ties",
        weight: 15,
        text: "Which of the following best demonstrates your ties to your home country?",
        options: [
          { label: "I own property in my home country", score: 15 },
          { label: "I have dependants (children, spouse) relying on me", score: 13 },
          { label: "I have a confirmed return ticket and active employment", score: 10 },
          { label: "I have no significant property or family ties", score: 3 },
        ],
        improvementNote: "Ties to home country are critical. Officers must be satisfied you will leave the UK at the end of your visit. Property ownership, family responsibility, and employer confirmation of return are the three strongest evidence types.",
      },
      {
        id: "duration",
        weight: 5,
        text: "How long do you intend to stay in the United Kingdom?",
        options: [
          { label: "1–14 days", score: 5 },
          { label: "15–30 days", score: 5 },
          { label: "31–90 days", score: 4 },
          { label: "More than 90 days", score: 2 },
        ],
        improvementNote: "Shorter, well-planned visits with clear itineraries are viewed more favourably than extended open-ended stays, particularly for first-time applicants.",
      },
      {
        id: "accommodation",
        weight: 5,
        text: "What is your accommodation arrangement in the UK?",
        options: [
          { label: "Confirmed hotel or rental booking for full duration", score: 5 },
          { label: "Staying with family or friends (with invitation letter)", score: 4 },
          { label: "Partially arranged accommodation", score: 2 },
        ],
        improvementNote: "Full accommodation confirmation for every night of your stay removes officer doubt about your itinerary. Pre-book and include printout references of all reservations.",
      },
      {
        id: "refusals",
        weight: 5,
        text: "Have you previously been refused a visa to the UK or any other country?",
        options: [
          { label: "No prior refusals", score: 5 },
          { label: "Refused once, circumstances have since changed", score: 2 },
          { label: "Refused more than once", score: 0 },
        ],
        improvementNote: "Prior refusals must be declared truthfully. If refused before, address the reason explicitly in your cover letter and demonstrate that the circumstances have changed with new supporting evidence.",
      },
    ],
  },
  {
    code: "US",
    name: "United States",
    region: "Americas",
    visaType: "B1/B2 Tourist & Business Visa",
    maxScore: 90,
    questions: [
      {
        id: "purpose",
        weight: 10,
        text: "What is the primary purpose of your visit to the United States?",
        options: [
          { label: "Tourism, vacation, or visiting attractions", score: 10 },
          { label: "Visiting family or friends who are US citizens or residents", score: 8 },
          { label: "Attending a business meeting, conference, or training", score: 9 },
          { label: "Medical treatment at a US hospital or clinic", score: 7, note: "Doctor's letter and financial capacity proof required" },
        ],
        improvementNote: "The consular officer must believe your stated purpose aligns with your overall profile. Tourism is straightforward, but your finances, employment, and ties must all support a temporary stay.",
      },
      {
        id: "employment",
        weight: 15,
        text: "What is your current employment status?",
        options: [
          { label: "Employed full-time for more than 2 years with the same employer", score: 15 },
          { label: "Self-employed with established business history", score: 12 },
          { label: "Employed less than 2 years or recently changed employer", score: 8 },
          { label: "Student with enrollment confirmation and financial support", score: 7 },
          { label: "Unemployed", score: 1 },
        ],
        improvementNote: "The US B visa is heavily interview-based. Stable employment is among the most persuasive evidence that you will return home. Bring your employment letter, pay stubs, and leave approval.",
      },
      {
        id: "finances",
        weight: 15,
        text: "How do your finances look for this trip?",
        options: [
          { label: "Strong bank history with funds exceeding $5,000 USD", score: 15 },
          { label: "Adequate funds between $2,000–$5,000 USD", score: 10 },
          { label: "Minimum funds, relying partly on a sponsor", score: 5 },
          { label: "Funds below $2,000 USD", score: 1 },
        ],
        improvementNote: "US consular officers look for evidence you can fund your entire trip without working illegally. Statements should show stable income — not a sudden large deposit — over at least 6 months.",
      },
      {
        id: "travel_history",
        weight: 10,
        text: "Which best describes your international travel history?",
        options: [
          { label: "Prior US visa holder or multiple international trips", score: 10 },
          { label: "Travelled to 2–5 countries in the past 5 years", score: 7 },
          { label: "Travelled internationally once or twice", score: 4 },
          { label: "No prior international travel", score: 1 },
        ],
        improvementNote: "Prior US visa approvals — even expired ones — carry significant weight in interview. If you have none, demonstrate strong ties and a well-funded, short, specific itinerary.",
      },
      {
        id: "home_ties",
        weight: 20,
        text: "What evidence of ties to your home country can you present?",
        options: [
          { label: "Property ownership, family, and confirmed employment", score: 20 },
          { label: "Active employment and immediate family dependants", score: 15 },
          { label: "Enrolled in an academic programme with return obligation", score: 10 },
          { label: "Minimal demonstrable ties to home country", score: 2 },
        ],
        improvementNote: "Section 214(b) of the US Immigration and Nationality Act presumes every visa applicant intends to immigrate. The burden is on you to prove otherwise. Property, family responsibilities, and employment together form the strongest case.",
      },
      {
        id: "interview",
        weight: 10,
        text: "How prepared are you for the consular interview?",
        options: [
          { label: "Fully prepared with all documents and a clear, concise narrative", score: 10 },
          { label: "Documents ready but unsure about interview responses", score: 6 },
          { label: "Minimal preparation so far", score: 2 },
        ],
        improvementNote: "The B1/B2 interview is typically 2–5 minutes. Your answers must be brief, confident, and consistent with your documents. Practice stating your purpose, duration, and return plans clearly.",
      },
      {
        id: "prior_refusals",
        weight: 10,
        text: "Have you previously been refused a US visa or been denied entry?",
        options: [
          { label: "No prior US refusals or entry denials", score: 10 },
          { label: "Refused once, with materially changed circumstances", score: 4 },
          { label: "Multiple refusals or a prior deportation / overstay", score: 0 },
        ],
        improvementNote: "A prior overstay or deportation creates a near-insurmountable barrier for most applicants. If you have a prior refusal, address the reason directly in your application and demonstrate what has changed.",
      },
    ],
  },
  {
    code: "DE",
    name: "Germany (Schengen)",
    region: "Europe",
    visaType: "Schengen Tourist Visa (Type C)",
    maxScore: 85,
    questions: [
      {
        id: "purpose",
        weight: 10,
        text: "What is the primary reason for your visit to Germany or the Schengen Area?",
        options: [
          { label: "Tourism and cultural travel within the Schengen Zone", score: 10 },
          { label: "Visiting family or friends resident in Germany", score: 8, note: "Verpflichtungserklärung (declaration of commitment) from host may be required" },
          { label: "Attending a business event, trade fair, or professional meeting", score: 9 },
          { label: "Transit through a German airport", score: 7 },
        ],
        improvementNote: "Germany processes the highest volume of Schengen applications. Tourism and business are the most straightforward categories. Ensure your stated purpose is supported by a detailed day-by-day itinerary.",
      },
      {
        id: "insurance",
        weight: 10,
        text: "Have you arranged travel health insurance for your Schengen trip?",
        options: [
          { label: "Yes — policy covers €30,000+ and all Schengen countries", score: 10 },
          { label: "Yes — but coverage is below €30,000 or limited in scope", score: 5 },
          { label: "Not yet arranged", score: 0 },
        ],
        improvementNote: "Travel medical insurance covering a minimum of €30,000 is a mandatory requirement — not optional — for all Schengen visa applications. The policy must cover the full duration of stay and all member states you plan to visit.",
      },
      {
        id: "accommodation",
        weight: 10,
        text: "What is your accommodation status for the entire Schengen trip?",
        options: [
          { label: "Confirmed hotel bookings for every night of the trip", score: 10 },
          { label: "Staying with family/friend (with signed invitation)", score: 8 },
          { label: "Partially confirmed accommodation", score: 4 },
          { label: "No confirmed accommodation yet", score: 0 },
        ],
        improvementNote: "German embassies require proof of accommodation for every night. Partially confirmed itineraries are a common reason for rejection. Use refundable hotel bookings if your plans are not yet finalised.",
      },
      {
        id: "finances",
        weight: 15,
        text: "How do your finances support this trip?",
        options: [
          { label: "Bank statements showing €45+ per day of travel with consistent history", score: 15 },
          { label: "Adequate funds with minor irregularities in statement history", score: 9 },
          { label: "Sponsor covering costs with their own financial declaration", score: 7 },
          { label: "Funds appear borderline relative to trip duration", score: 3 },
        ],
        improvementNote: "Germany's guideline is approximately €45 per day of stay as a minimum. Statements should be from the last 3 months and show regular salary deposits, not sudden large transfers which raise fraud flags.",
      },
      {
        id: "employment",
        weight: 15,
        text: "What is your current employment or professional situation?",
        options: [
          { label: "Employed with approved leave from employer for exact travel dates", score: 15 },
          { label: "Self-employed with active business registration and tax returns", score: 12 },
          { label: "Student with enrollment letter and financial guarantee", score: 9 },
          { label: "Retired with pension documents", score: 11 },
          { label: "Currently unemployed", score: 2 },
        ],
        improvementNote: "A No-Objection Certificate or leave approval letter from your employer — specifically covering your travel dates — is one of the most effective documents in a Schengen application.",
      },
      {
        id: "travel_history",
        weight: 10,
        text: "What is your prior Schengen or international travel history?",
        options: [
          { label: "Have previously held a Schengen visa with no overstays", score: 10 },
          { label: "Have travelled internationally but not to Schengen countries", score: 6 },
          { label: "Hold or have held a US or UK visa", score: 8 },
          { label: "No prior international travel", score: 2 },
        ],
        improvementNote: "A clean prior Schengen history is a strong positive signal. If applying for the first time, demonstrating a history of respecting visa conditions in other countries (US, UK, Gulf) partially compensates.",
      },
      {
        id: "home_ties",
        weight: 15,
        text: "What ties do you have to your home country that ensure your return?",
        options: [
          { label: "Property ownership and immediate family dependants", score: 15 },
          { label: "Active employment and family responsibility", score: 12 },
          { label: "Student enrolled in a course commencing after the trip", score: 8 },
          { label: "No significant or demonstrable ties", score: 2 },
        ],
        improvementNote: "Return intention is assessed holistically. Property deeds, family registration documents, and confirmed employment are the three strongest proof types for Schengen applications.",
      },
    ],
  },
  {
    code: "CA",
    name: "Canada",
    region: "Americas",
    visaType: "Temporary Resident Visa (TRV)",
    maxScore: 85,
    questions: [
      {
        id: "purpose",
        weight: 10,
        text: "What is your primary reason for visiting Canada?",
        options: [
          { label: "Tourism and exploring Canada's attractions", score: 10 },
          { label: "Visiting family or friends who are Canadian citizens or PRs", score: 8 },
          { label: "Attending a business event, conference, or meetings", score: 9 },
          { label: "Short-term academic course or program", score: 7 },
        ],
        improvementNote: "IRCC assesses whether your visit is genuine and temporary. Pair any stated purpose with a detailed itinerary and corresponding supporting documents.",
      },
      {
        id: "finances",
        weight: 15,
        text: "How would you describe your financial capacity for the trip?",
        options: [
          { label: "Strong bank history exceeding CAD 10,000 for the travel period", score: 15 },
          { label: "Adequate funds between CAD 5,000–10,000", score: 10 },
          { label: "A Canadian sponsor or host covering my expenses", score: 7 },
          { label: "Funds below CAD 5,000", score: 2 },
        ],
        improvementNote: "Canada expects roughly CAD 100–150 per day of stay as a minimum financial benchmark. Include 6 months of bank statements and avoid large unexplained deposits immediately before applying.",
      },
      {
        id: "employment",
        weight: 15,
        text: "What is your current employment status?",
        options: [
          { label: "Stably employed, 2+ years, with approved leave confirmation", score: 15 },
          { label: "Self-employed with business documentation and filed taxes", score: 11 },
          { label: "Student with enrollment and financial sponsorship", score: 8 },
          { label: "Retired with pension and fixed income", score: 10 },
          { label: "Unemployed", score: 1 },
        ],
        improvementNote: "Employment documents — especially an employer's letter confirming your role, salary, approved leave, and expected return date — are among the most persuasive elements in a Canadian TRV application.",
      },
      {
        id: "travel_history",
        weight: 10,
        text: "What is your prior travel history?",
        options: [
          { label: "Have previously visited Canada or hold a US/UK/Schengen visa", score: 10 },
          { label: "Have travelled internationally to multiple destinations", score: 7 },
          { label: "Limited international travel", score: 4 },
          { label: "No prior international travel", score: 1 },
        ],
        improvementNote: "Canada treats prior compliance with visa conditions in other countries as a strong positive indicator. If you hold or have held a US, UK, or Schengen visa, ensure copies are included in your application.",
      },
      {
        id: "home_ties",
        weight: 20,
        text: "What evidence of ties to your home country can you provide?",
        options: [
          { label: "Property ownership, immediate family, and active employment all present", score: 20 },
          { label: "Strong employment and family ties", score: 15 },
          { label: "Student or young professional with institutional ties", score: 9 },
          { label: "Limited demonstrable ties to home", score: 2 },
        ],
        improvementNote: "Canada's visa refusal rate is significant. The single most common reason is insufficient evidence of ties to the home country. Document every property, family relationship, and employment obligation you have.",
      },
      {
        id: "invitation",
        weight: 10,
        text: "Do you have an invitation or host letter from someone in Canada?",
        options: [
          { label: "Yes — formal letter with host's PR/citizenship and financial details", score: 10 },
          { label: "Informal letter from a contact in Canada", score: 5 },
          { label: "No host — independent travel", score: 7 },
        ],
        improvementNote: "If visiting friends or family, a strong invitation letter from a Canadian PR or citizen with their status documents significantly supports your application, particularly for first-time applicants.",
      },
      {
        id: "refusals",
        weight: 5,
        text: "Have you previously been refused a Canadian visa or any other visa?",
        options: [
          { label: "No prior refusals anywhere", score: 5 },
          { label: "Refused once, circumstances improved since", score: 2 },
          { label: "Multiple refusals", score: 0 },
        ],
        improvementNote: "Any prior Canadian refusal must be declared on the application form. Address the previous refusal reason specifically in your cover letter.",
      },
    ],
  },
  {
    code: "AU",
    name: "Australia",
    region: "Asia Pacific",
    visaType: "Visitor Visa (Subclass 600)",
    maxScore: 85,
    questions: [
      {
        id: "purpose",
        weight: 10,
        text: "What is the main purpose of your visit to Australia?",
        options: [
          { label: "Tourism and holiday travel", score: 10 },
          { label: "Visiting family or friends who are Australian citizens or residents", score: 8 },
          { label: "Business activities such as meetings, conferences, or negotiations", score: 9 },
          { label: "Short-term training or professional development", score: 7 },
        ],
        improvementNote: "The Department of Home Affairs requires that your stated purpose is supported by evidence. Tourism applicants should include a detailed itinerary, flight bookings, and accommodation confirmations.",
      },
      {
        id: "finances",
        weight: 15,
        text: "How do your finances support your Australian visit?",
        options: [
          { label: "Strong funds — AUD 5,000+ with consistent 6-month bank history", score: 15 },
          { label: "Moderate funds — AUD 3,000–5,000 with adequate statements", score: 10 },
          { label: "A sponsor in Australia is covering costs (with their declaration)", score: 7 },
          { label: "Limited funds relative to the duration of stay", score: 2 },
        ],
        improvementNote: "Australia expects applicants to show they can genuinely fund their stay without working. Bank statements should span 6 months and reflect regular income.",
      },
      {
        id: "employment",
        weight: 15,
        text: "What is your employment status at the time of application?",
        options: [
          { label: "Employed in a stable, long-term role with leave approval for travel dates", score: 15 },
          { label: "Self-employed with ABN equivalent and active business records", score: 11 },
          { label: "Student with institution letter and financial support evidence", score: 8 },
          { label: "Retired with pension documentation", score: 11 },
          { label: "Unemployed or between roles", score: 1 },
        ],
        improvementNote: "Stable employment is one of the clearest signals that you will return home after your visit. Include your employer's letter, recent payslips, and written leave approval specific to your travel dates.",
      },
      {
        id: "health_character",
        weight: 10,
        text: "Are you able to meet Australia's health and character requirements?",
        options: [
          { label: "No criminal record and meet health standards", score: 10 },
          { label: "Minor past conviction, fully disclosed", score: 4 },
          { label: "Significant criminal history or health condition", score: 0 },
        ],
        improvementNote: "Australia applies strict health and character tests. Any criminal conviction should be disclosed fully and accurately. Failure to disclose can result in permanent character-based refusal.",
      },
      {
        id: "travel_history",
        weight: 10,
        text: "What is your international travel history?",
        options: [
          { label: "Prior Australian visa with no overstays, or multiple trusted-country visas", score: 10 },
          { label: "Travelled to several countries without visa violations", score: 7 },
          { label: "Limited international travel", score: 4 },
          { label: "No prior international travel", score: 1 },
        ],
        improvementNote: "Australia's Department of Home Affairs views prior travel compliance very positively.",
      },
      {
        id: "home_ties",
        weight: 15,
        text: "What ties to your home country demonstrate you will return?",
        options: [
          { label: "Property ownership, family dependants, and active employment", score: 15 },
          { label: "Family and employment (no property)", score: 11 },
          { label: "Academic enrolment or institutional obligations in home country", score: 8 },
          { label: "No significant or documented ties", score: 2 },
        ],
        improvementNote: "Australia carefully scrutinises the risk of applicants overstaying. Include property title documents, family relationship certificates, and employer return-date letters.",
      },
      {
        id: "refusals",
        weight: 10,
        text: "Have you previously been refused an Australian visa or a visa from another country?",
        options: [
          { label: "No prior refusals", score: 10 },
          { label: "Refused once, with the underlying issue now resolved", score: 4 },
          { label: "Multiple refusals or prior overstay record", score: 0 },
        ],
        improvementNote: "Any prior refusal from Australia specifically must be declared and addressed in a covering letter.",
      },
    ],
  },
  {
    code: "AE",
    name: "United Arab Emirates",
    region: "Middle East",
    visaType: "Tourist Visa (On Arrival / eVisa)",
    maxScore: 60,
    questions: [
      {
        id: "passport",
        weight: 20,
        text: "What is the nationality of your passport?",
        options: [
          { label: "Visa-on-arrival eligible country (GCC, Western nations, etc.)", score: 20 },
          { label: "eVisa eligible country requiring prior application", score: 15 },
          { label: "Country requiring full embassy visa application", score: 8 },
        ],
        improvementNote: "UAE visa requirements vary dramatically by nationality. Check the official UAE ICA website for your specific nationality's requirements.",
      },
      {
        id: "finances",
        weight: 15,
        text: "What is your financial standing for the visit?",
        options: [
          { label: "Strong bank history and confirmed return ticket", score: 15 },
          { label: "Adequate funds and a return ticket", score: 10 },
          { label: "Funds are limited relative to the cost of UAE travel", score: 4 },
        ],
        improvementNote: "Carry evidence of at least AED 3,000 equivalent. Immigration officers at the border may ask for proof of sufficient funds.",
      },
      {
        id: "accommodation",
        weight: 10,
        text: "What is your accommodation arrangement in the UAE?",
        options: [
          { label: "Confirmed hotel bookings for the full stay", score: 10 },
          { label: "Staying with a UAE resident (with their visa or residency details)", score: 9 },
          { label: "Accommodation not yet confirmed", score: 3 },
        ],
        improvementNote: "Hotel confirmation details may be requested at the port of entry.",
      },
      {
        id: "return_ticket",
        weight: 10,
        text: "Do you have a confirmed return or onward travel ticket?",
        options: [
          { label: "Yes — confirmed return flight booked", score: 10 },
          { label: "One-way ticket with plans to depart via another country", score: 5 },
          { label: "No confirmed onward travel yet", score: 0 },
        ],
        improvementNote: "A confirmed return ticket is among the most important items for UAE entry. Entry may be refused without one.",
      },
      {
        id: "purpose",
        weight: 5,
        text: "What is the primary purpose of your visit to the UAE?",
        options: [
          { label: "Tourism, leisure, or shopping", score: 5 },
          { label: "Visiting family or friends resident in UAE", score: 5 },
          { label: "Business meetings (no local employment)", score: 4 },
        ],
        improvementNote: "The UAE permits business activities on a tourist visa but explicitly prohibits engaging in paid employment.",
      },
    ],
  },
  {
    code: "JP",
    name: "Japan",
    region: "Asia Pacific",
    visaType: "Temporary Visitor Visa",
    maxScore: 80,
    questions: [
      {
        id: "purpose",
        weight: 10,
        text: "What is the purpose of your visit to Japan?",
        options: [
          { label: "Tourism, sightseeing, and cultural travel", score: 10 },
          { label: "Visiting family or friends in Japan", score: 8, note: "Invitation letter from a Japan-resident required" },
          { label: "Business meetings or attending a conference", score: 9 },
          { label: "Short-term language study (under 90 days)", score: 8 },
        ],
        improvementNote: "For tourism, a complete day-by-day itinerary and pre-booked accommodation are expected even if not strictly mandatory for all nationalities.",
      },
      {
        id: "itinerary",
        weight: 15,
        text: "How detailed and confirmed is your travel itinerary?",
        options: [
          { label: "Full day-by-day itinerary with pre-booked hotels and transport", score: 15 },
          { label: "General plan with confirmed accommodation for most nights", score: 10 },
          { label: "Rough outline, details to be confirmed closer to travel", score: 4 },
          { label: "No itinerary prepared yet", score: 0 },
        ],
        improvementNote: "Japan consulates, particularly for applicants from South Asia, require very detailed documentation.",
      },
      {
        id: "finances",
        weight: 15,
        text: "How do your finances support your Japan visit?",
        options: [
          { label: "Bank statements showing ¥200,000+ equivalent with steady income", score: 15 },
          { label: "Adequate funds for trip duration with consistent history", score: 10 },
          { label: "Borderline funds with some irregularities", score: 4 },
          { label: "Insufficient funds relative to planned stay", score: 0 },
        ],
        improvementNote: "Japan's cost of living for tourists is approximately ¥15,000–¥25,000 per day.",
      },
      {
        id: "employment",
        weight: 15,
        text: "What is your employment status?",
        options: [
          { label: "Employed, stable role, with employer letter and approved leave", score: 15 },
          { label: "Self-employed with formal business documentation", score: 11 },
          { label: "Student with enrollment letter and financial sponsor", score: 9 },
          { label: "Retired with pension evidence", score: 11 },
          { label: "Unemployed", score: 1 },
        ],
        improvementNote: "An employer letter explicitly naming your Japan travel dates and confirming your return is highly effective.",
      },
      {
        id: "home_ties",
        weight: 15,
        text: "What demonstrable ties to your home country do you have?",
        options: [
          { label: "Property ownership, family dependants, and employment", score: 15 },
          { label: "Strong employment and immediate family", score: 11 },
          { label: "Academic obligations or institutional ties", score: 8 },
          { label: "Minimal demonstrable ties", score: 2 },
        ],
        improvementNote: "Return intent is the cornerstone of any short-stay Japan visa application.",
      },
      {
        id: "travel_history",
        weight: 10,
        text: "What is your prior international travel history?",
        options: [
          { label: "Prior Japan visa with clean record, or multiple trusted-nation visas", score: 10 },
          { label: "Travelled to several countries without visa violations", score: 7 },
          { label: "Limited international travel", score: 4 },
          { label: "First international trip", score: 1 },
        ],
        improvementNote: "Prior clean travel history — particularly to the US, UK, Schengen, or Australia — is viewed positively by Japan's consular staff.",
      },
    ],
  },
  {
    code: "FR",
    name: "France (Schengen)",
    region: "Europe",
    visaType: "Schengen Tourist Visa (Type C)",
    maxScore: 80,
    questions: [
      {
        id: "purpose",
        weight: 10,
        text: "What is the primary reason for your visit to France or the Schengen Area?",
        options: [
          { label: "Tourism and cultural visits to France", score: 10 },
          { label: "Visiting family or friends in France", score: 8 },
          { label: "Business or professional activities", score: 9 },
          { label: "Transit through France to another non-Schengen country", score: 7 },
        ],
        improvementNote: "France is the entry point of your Schengen visa if it is your primary destination or first port of entry.",
      },
      {
        id: "insurance",
        weight: 10,
        text: "Have you arranged the mandatory travel insurance?",
        options: [
          { label: "Yes — policy covers €30,000 minimum for all Schengen countries", score: 10 },
          { label: "Insurance arranged but below the minimum coverage", score: 4 },
          { label: "Not yet arranged", score: 0 },
        ],
        improvementNote: "Travel insurance covering a minimum of €30,000 across all Schengen member states is a non-negotiable requirement.",
      },
      {
        id: "finances",
        weight: 15,
        text: "How do your finances support your stay in France?",
        options: [
          { label: "Consistent bank statements with €65+ per day of planned stay", score: 15 },
          { label: "Adequate funds with some minor inconsistencies", score: 9 },
          { label: "Funds sponsored by a French resident or organisation", score: 7 },
          { label: "Funds appear insufficient for planned duration", score: 2 },
        ],
        improvementNote: "France applies a benchmark of approximately €65 per day of stay.",
      },
      {
        id: "accommodation",
        weight: 10,
        text: "What is your accommodation arrangement for the full trip?",
        options: [
          { label: "Confirmed hotel bookings for every night of the stay", score: 10 },
          { label: "Staying with a French resident (with invitation and their status proof)", score: 8 },
          { label: "Partially confirmed accommodation", score: 4 },
          { label: "No confirmed accommodation", score: 0 },
        ],
        improvementNote: "The French consulate requires proof of accommodation for every night of the requested stay.",
      },
      {
        id: "employment",
        weight: 15,
        text: "What is your employment or professional status?",
        options: [
          { label: "Employed full-time with leave approval for specific travel dates", score: 15 },
          { label: "Self-employed with registered entity and tax filings", score: 11 },
          { label: "Student with institution letter and sponsor guarantee", score: 9 },
          { label: "Retired with regular pension evidence", score: 11 },
          { label: "Unemployed", score: 2 },
        ],
        improvementNote: "A No-Objection Certificate from your employer — specifying exactly the travel dates — is among the highest-impact documents in a French Schengen application.",
      },
      {
        id: "home_ties",
        weight: 15,
        text: "What ties to your home country can you document?",
        options: [
          { label: "Property, immediate family, and confirmed employment", score: 15 },
          { label: "Active employment and close family ties", score: 11 },
          { label: "Academic or professional obligation requiring return", score: 8 },
          { label: "Minimal documented ties", score: 2 },
        ],
        improvementNote: "Property ownership documentation, spouse/children registrations, and employer return confirmation are the three most effective proof types.",
      },
      {
        id: "travel_history",
        weight: 5,
        text: "What is your prior Schengen or international visa history?",
        options: [
          { label: "Prior Schengen visa with no overstay or violation", score: 5 },
          { label: "Travelled internationally but not to Schengen zone", score: 3 },
          { label: "No prior international travel", score: 1 },
        ],
        improvementNote: "If this is your first Schengen application, holding a clean US or UK visa record partially substitutes as evidence of your intent to comply with immigration conditions.",
      },
    ],
  },
  {
    code: "NZ",
    name: "New Zealand",
    region: "Asia Pacific",
    visaType: "Visitor Visa",
    maxScore: 75,
    questions: [
      {
        id: "purpose",
        weight: 10,
        text: "What is the main reason for your visit to New Zealand?",
        options: [
          { label: "Tourism, outdoor activities, or sightseeing", score: 10 },
          { label: "Visiting family or friends resident in New Zealand", score: 8 },
          { label: "Business meetings or short-term professional activities", score: 9 },
          { label: "Attending an event or short-term course", score: 7 },
        ],
        improvementNote: "Immigration New Zealand assesses whether your visit is genuinely temporary. Include a day-by-day itinerary and pre-booked accommodation.",
      },
      {
        id: "finances",
        weight: 15,
        text: "How do your finances support your visit?",
        options: [
          { label: "NZD 1,000+ per month of planned stay, with solid 3–6 month statements", score: 15 },
          { label: "Adequate funds with some gaps in statement history", score: 9 },
          { label: "New Zealand-based sponsor covering expenses", score: 7 },
          { label: "Funds appear insufficient for the intended stay", score: 2 },
        ],
        improvementNote: "INZ requires approximately NZD 1,000 per month of planned stay.",
      },
      {
        id: "health_character",
        weight: 15,
        text: "Can you meet New Zealand's health and character requirements?",
        options: [
          { label: "No criminal record and fully healthy", score: 15 },
          { label: "Minor historical conviction, fully disclosed", score: 5 },
          { label: "Criminal record or health condition that may require waiver", score: 0 },
        ],
        improvementNote: "New Zealand has strict good-character requirements. Any criminal conviction — even spent or minor ones — must be declared.",
      },
      {
        id: "employment",
        weight: 15,
        text: "What is your employment or professional status?",
        options: [
          { label: "Stable employment with approved leave for travel dates", score: 15 },
          { label: "Self-employed with documented business activity", score: 11 },
          { label: "Student with enrollment and financial sponsorship", score: 8 },
          { label: "Retired with consistent pension income", score: 11 },
          { label: "Unemployed or between positions", score: 2 },
        ],
        improvementNote: "An employer letter confirming your approved leave period and expected return to work is one of the most effective documents.",
      },
      {
        id: "home_ties",
        weight: 15,
        text: "What ties demonstrate your intention to return home after the visit?",
        options: [
          { label: "Property, family dependants, and confirmed employment", score: 15 },
          { label: "Active employment and close family relationships", score: 11 },
          { label: "Institutional ties such as active academic enrolment", score: 7 },
          { label: "Limited demonstrable ties to home country", score: 2 },
        ],
        improvementNote: "INZ specifically looks for evidence that your centre of life is in your home country.",
      },
      {
        id: "travel_history",
        weight: 5,
        text: "What is your prior international travel history?",
        options: [
          { label: "Prior NZ or Australian visa with clean record", score: 5 },
          { label: "Multiple international trips with no violations", score: 4 },
          { label: "Limited or no prior international travel", score: 2 },
        ],
        improvementNote: "Clean compliance with other immigration conditions — particularly for Australia, UK, or the US — is viewed positively by INZ.",
      },
    ],
  },
  {
    code: "SG",
    name: "Singapore",
    region: "Asia Pacific",
    visaType: "Short-Term Visit Pass",
    maxScore: 55,
    questions: [
      {
        id: "passport",
        weight: 20,
        text: "What nationality is your passport?",
        options: [
          { label: "Visa-free entry nationality (most Western, ASEAN countries)", score: 20 },
          { label: "eVisa / pre-approved entry required", score: 12 },
          { label: "Requires full visa application through ICA", score: 6 },
        ],
        improvementNote: "Singapore grants visa-free access to citizens of over 160 countries. For nationalities requiring a visa, apply through the ICA portal well in advance.",
      },
      {
        id: "finances",
        weight: 15,
        text: "What is your financial standing for the visit?",
        options: [
          { label: "Sufficient funds and a confirmed return ticket", score: 15 },
          { label: "Moderate funds with a return ticket", score: 10 },
          { label: "Funds appear limited for Singapore's cost of living", score: 3 },
        ],
        improvementNote: "Carry evidence of at least SGD 1,000 per week of stay and a confirmed return ticket.",
      },
      {
        id: "accommodation",
        weight: 10,
        text: "What is your accommodation arrangement in Singapore?",
        options: [
          { label: "Confirmed hotel or short-term rental for full stay", score: 10 },
          { label: "Staying with a Singapore resident (with their contact details)", score: 9 },
          { label: "Accommodation not yet confirmed", score: 2 },
        ],
        improvementNote: "ICA may ask for accommodation details at the immigration counter.",
      },
      {
        id: "return_ticket",
        weight: 10,
        text: "Do you have a confirmed return or onward ticket?",
        options: [
          { label: "Yes — confirmed return or onward flight is booked", score: 10 },
          { label: "Travel plans are still open-ended", score: 0 },
        ],
        improvementNote: "A confirmed onward or return ticket is effectively mandatory for Singapore entry.",
      },
    ],
  },
];

export const regionList = ["All Regions", "Europe", "Americas", "Asia Pacific", "Middle East", "Africa", "South Asia"];
export const visaTypeList = ["All visa types", "Tourist", "Business", "Student", "Work"];

// Passport validity rules per destination (minimum months required beyond travel dates)
export const passportValidityRules = {
  "GB": { minMonthsBeyond: 0, note: "Must be valid for the duration of your stay." },
  "US": { minMonthsBeyond: 6, note: "Generally must be valid for 6 months beyond your stay." },
  "DE": { minMonthsBeyond: 3, note: "Must be valid for at least 3 months beyond intended departure from Schengen." },
  "FR": { minMonthsBeyond: 3, note: "Must be valid for at least 3 months beyond intended departure from Schengen." },
  "CA": { minMonthsBeyond: 0, note: "Must be valid for the duration of your stay." },
  "AU": { minMonthsBeyond: 6, note: "Should be valid for at least 6 months beyond your stay." },
  "AE": { minMonthsBeyond: 6, note: "Must be valid for at least 6 months from date of entry." },
  "JP": { minMonthsBeyond: 0, note: "Must be valid for the duration of your stay." },
  "NZ": { minMonthsBeyond: 1, note: "Must be valid for at least 1 month beyond your planned departure." },
  "SG": { minMonthsBeyond: 6, note: "Must be valid for at least 6 months from date of entry." },
};

